import React, { useState, useEffect, useCallback, useRef } from 'react';
import { GoogleMap, LoadScript, Polygon, Marker, Polyline, Circle, DrawingManager } from '@react-google-maps/api';
import { workAreaAPI, cityAPI } from '../../services/api';
import Navbar from '../../components/Navbar';

const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
const libraries = ['drawing'];

// Snap-to-corner configuration - auto-snap when within 20 meters of existing corner
const SNAP_THRESHOLD_METERS = 20;

const mapContainerStyle = {
  width: '100%',
  height: '500px',
  borderRadius: '12px'
};

const defaultCenter = {
  lat: 27.1767, // Agra default
  lng: 78.0081
};

// City coordinates for centering map
const cityCoordinates = {
  'Agra': { lat: 27.1767, lng: 78.0081 },
  'Mumbai': { lat: 19.0760, lng: 72.8777 },
  'Delhi': { lat: 28.6139, lng: 77.2090 },
  'Bangalore': { lat: 12.9716, lng: 77.5946 },
  'Chennai': { lat: 13.0827, lng: 80.2707 },
  'Kolkata': { lat: 22.5726, lng: 88.3639 },
  'Hyderabad': { lat: 17.3850, lng: 78.4867 },
  'Pune': { lat: 18.5204, lng: 73.8567 },
};

const WorkAreaManagement = () => {
  const [workAreas, setWorkAreas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingArea, setEditingArea] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    city: '',
    state: '',
    description: '',
    isActive: true,
    boundaries: null
  });
  const [search, setSearch] = useState('');
  const [filterCity, setFilterCity] = useState('');
  const [cities, setCities] = useState([]);
  const [mapCenter, setMapCenter] = useState(defaultCenter);
  const [drawingMode, setDrawingMode] = useState(null);
  const [currentPolygon, setCurrentPolygon] = useState(null);
  const [snappedCount, setSnappedCount] = useState(0); // Track how many points were snapped

  // Batch creation state
  const [showBatchModal, setShowBatchModal] = useState(false);
  const [batchCity, setBatchCity] = useState('');
  const [batchState, setBatchState] = useState('');
  const [batchWorkAreas, setBatchWorkAreas] = useState([]);
  const [newWorkAreaName, setNewWorkAreaName] = useState('');
  const [newWorkAreaDescription, setNewWorkAreaDescription] = useState('');
  const [currentBatchIndex, setCurrentBatchIndex] = useState(-1); // -1 means still adding names
  const [batchDrawingMode, setBatchDrawingMode] = useState(false);
  const [savingBatch, setSavingBatch] = useState(false);

  const drawingManagerRef = useRef(null);
  const mapRef = useRef(null);

  // Custom drawing state for real-time snap detection
  const [customDrawingPoints, setCustomDrawingPoints] = useState([]); // Points being drawn
  const [isCustomDrawing, setIsCustomDrawing] = useState(false); // Whether custom drawing is active
  const [snapIndicator, setSnapIndicator] = useState(null); // {lat, lng, type: 'corner'|'edge'} - shows snap preview
  const [lastSnapInfo, setLastSnapInfo] = useState(null); // Track what was snapped for feedback

  // Batch custom drawing state
  const [batchCustomDrawingPoints, setBatchCustomDrawingPoints] = useState([]);
  const [isBatchCustomDrawing, setIsBatchCustomDrawing] = useState(false);
  const [batchSnapIndicator, setBatchSnapIndicator] = useState(null);

  useEffect(() => {
    fetchWorkAreas();
    fetchCities();
  }, [search, filterCity]);

  const fetchWorkAreas = async () => {
    try {
      setLoading(true);
      const response = await workAreaAPI.getAllWorkAreas({ search, city: filterCity });
      setWorkAreas(response.data.data.workAreas);
    } catch (error) {
      alert('Failed to fetch work areas');
    } finally {
      setLoading(false);
    }
  };

  const fetchCities = async () => {
    try {
      const response = await cityAPI.getActiveCities();
      setCities(response.data.data.cities);
    } catch (error) {
      console.error('Failed to fetch cities');
    }
  };

  // ============ SIMPLE SNAP-TO-EDGE FUNCTIONS ============

  // Calculate distance between two lat/lng points in meters (Haversine formula)
  const getDistanceInMeters = (lat1, lng1, lat2, lng2) => {
    const R = 6371000; // Earth's radius in meters
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLng = (lng2 - lng1) * Math.PI / 180;
    const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
              Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
              Math.sin(dLng / 2) * Math.sin(dLng / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  };

  // Get nearest point on line segment AB from point P
  const getNearestPointOnSegment = (pLng, pLat, aLng, aLat, bLng, bLat) => {
    const dx = bLng - aLng;
    const dy = bLat - aLat;
    const lengthSq = dx * dx + dy * dy;

    if (lengthSq === 0) return { lng: aLng, lat: aLat };

    let t = ((pLng - aLng) * dx + (pLat - aLat) * dy) / lengthSq;
    t = Math.max(0, Math.min(1, t)); // Clamp to segment

    return { lng: aLng + t * dx, lat: aLat + t * dy };
  };

  // Auto-snap coordinates to nearest existing CORNERS ONLY (post-processing fallback)
  const snapCoordinatesToEdges = useCallback((coordinates, excludeAreaId = null, additionalAreas = []) => {
    const excludeIdStr = excludeAreaId ? excludeAreaId.toString() : null;

    const areasToCheck = [
      ...workAreas.filter(a => {
        const areaIdStr = a._id ? a._id.toString() : null;
        return areaIdStr !== excludeIdStr && a.boundaries?.coordinates?.[0];
      }),
      ...additionalAreas.filter(a => a.boundaries?.coordinates?.[0])
    ];

    if (areasToCheck.length === 0) {
      return { coords: coordinates, snappedCount: 0 };
    }

    let snappedCount = 0;
    const snappedCoords = coordinates.map(([lng, lat], idx) => {
      if (idx === coordinates.length - 1) return [lng, lat];

      let nearestDist = Infinity;
      let nearestPoint = null;

      for (const area of areasToCheck) {
        const areaCoords = area.boundaries.coordinates[0];

        // CORNERS ONLY - no edge snapping
        for (let i = 0; i < areaCoords.length - 1; i++) {
          const [cornerLng, cornerLat] = areaCoords[i];
          const dist = getDistanceInMeters(lat, lng, cornerLat, cornerLng);

          if (dist < nearestDist && dist <= SNAP_THRESHOLD_METERS) {
            nearestDist = dist;
            nearestPoint = { lng: cornerLng, lat: cornerLat };
          }
        }
      }

      if (nearestPoint) {
        snappedCount++;
        return [nearestPoint.lng, nearestPoint.lat];
      }
      return [lng, lat];
    });

    if (snappedCoords.length > 1) {
      snappedCoords[snappedCoords.length - 1] = [...snappedCoords[0]];
    }

    return { coords: snappedCoords, snappedCount };
  }, [workAreas, getDistanceInMeters]);

  // ============ REAL-TIME SNAP DETECTION FOR CUSTOM DRAWING ============

  // Find nearest snap point (CORNERS ONLY) for a given coordinate - used in real-time during drawing
  const findNearestSnapPoint = useCallback((lat, lng, excludeAreaId = null, additionalAreas = []) => {
    const excludeIdStr = excludeAreaId ? excludeAreaId.toString() : null;

    const areasToCheck = [
      ...workAreas.filter(a => {
        const areaIdStr = a._id ? a._id.toString() : null;
        return areaIdStr !== excludeIdStr && a.boundaries?.coordinates?.[0];
      }),
      ...additionalAreas.filter(a => a.boundaries?.coordinates?.[0])
    ];

    if (areasToCheck.length === 0) return null;

    let nearestDist = Infinity;
    let nearestPoint = null;
    let snapAreaName = null;

    for (const area of areasToCheck) {
      const areaCoords = area.boundaries.coordinates[0];

      // Check CORNERS ONLY (no edge snapping)
      for (let i = 0; i < areaCoords.length - 1; i++) {
        const [cornerLng, cornerLat] = areaCoords[i];
        const dist = getDistanceInMeters(lat, lng, cornerLat, cornerLng);

        if (dist < nearestDist && dist <= SNAP_THRESHOLD_METERS) {
          nearestDist = dist;
          nearestPoint = { lat: cornerLat, lng: cornerLng };
          snapAreaName = area.name;
        }
      }
    }

    if (nearestPoint) {
      return { ...nearestPoint, type: 'corner', areaName: snapAreaName, distance: nearestDist };
    }
    return null;
  }, [workAreas, getDistanceInMeters]);

  // Handle map click for custom drawing with real-time snap
  const handleMapClickForDrawing = useCallback((event) => {
    if (!isCustomDrawing) return;

    const clickLat = event.latLng.lat();
    const clickLng = event.latLng.lng();

    // Check if clicking near the first point to close the polygon
    if (customDrawingPoints.length >= 3) {
      const firstPoint = customDrawingPoints[0];
      const distToFirst = getDistanceInMeters(clickLat, clickLng, firstPoint.lat, firstPoint.lng);

      if (distToFirst < 50) { // Within 50 meters of first point = close polygon
        // Complete the polygon
        const coordinates = customDrawingPoints.map(p => [p.lng, p.lat]);
        coordinates.push(coordinates[0]); // Close polygon

        setFormData(prev => ({
          ...prev,
          boundaries: {
            type: 'Polygon',
            coordinates: [coordinates]
          }
        }));

        // Count how many points were snapped
        const snappedPoints = customDrawingPoints.filter(p => p.snapped).length;
        setSnappedCount(snappedPoints);

        // Reset drawing state
        setIsCustomDrawing(false);
        setCustomDrawingPoints([]);
        setSnapIndicator(null);
        setDrawingMode(null);

        if (snappedPoints > 0) {
          alert(`✅ Polygon completed! ${snappedPoints} point(s) snapped to existing areas.`);
        }
        return;
      }
    }

    // Check for snap to existing corners/edges
    const snapResult = findNearestSnapPoint(clickLat, clickLng, editingArea?._id);

    let newPoint;
    if (snapResult) {
      // SNAP: Use the snapped coordinates
      newPoint = {
        lat: snapResult.lat,
        lng: snapResult.lng,
        snapped: true,
        snapType: snapResult.type,
        snapArea: snapResult.areaName
      };
      console.log(`🧲 SNAPPED to ${snapResult.type} of "${snapResult.areaName}" (${snapResult.distance.toFixed(1)}m away)`);
      setLastSnapInfo({ type: snapResult.type, areaName: snapResult.areaName });
    } else {
      // NO SNAP: Use original click coordinates
      newPoint = {
        lat: clickLat,
        lng: clickLng,
        snapped: false
      };
      setLastSnapInfo(null);
    }

    setCustomDrawingPoints(prev => [...prev, newPoint]);
  }, [isCustomDrawing, customDrawingPoints, editingArea, findNearestSnapPoint, getDistanceInMeters]);

  // Handle mouse move to show snap preview indicator
  const handleMapMouseMove = useCallback((event) => {
    if (!isCustomDrawing) {
      setSnapIndicator(null);
      return;
    }

    const lat = event.latLng.lat();
    const lng = event.latLng.lng();

    const snapResult = findNearestSnapPoint(lat, lng, editingArea?._id);

    if (snapResult) {
      setSnapIndicator({
        lat: snapResult.lat,
        lng: snapResult.lng,
        type: snapResult.type,
        areaName: snapResult.areaName
      });
    } else {
      setSnapIndicator(null);
    }
  }, [isCustomDrawing, editingArea, findNearestSnapPoint]);

  // Handle double-click to complete polygon
  const handleMapDoubleClick = useCallback((event) => {
    if (!isCustomDrawing || customDrawingPoints.length < 3) return;

    // Complete the polygon with current points
    const coordinates = customDrawingPoints.map(p => [p.lng, p.lat]);
    coordinates.push(coordinates[0]); // Close polygon

    setFormData(prev => ({
      ...prev,
      boundaries: {
        type: 'Polygon',
        coordinates: [coordinates]
      }
    }));

    const snappedPoints = customDrawingPoints.filter(p => p.snapped).length;
    setSnappedCount(snappedPoints);

    setIsCustomDrawing(false);
    setCustomDrawingPoints([]);
    setSnapIndicator(null);
    setDrawingMode(null);

    if (snappedPoints > 0) {
      alert(`✅ Polygon completed! ${snappedPoints} point(s) snapped to existing areas.`);
    }
  }, [isCustomDrawing, customDrawingPoints]);

  // Start custom drawing mode
  const startCustomDrawing = useCallback(() => {
    setIsCustomDrawing(true);
    setCustomDrawingPoints([]);
    setSnapIndicator(null);
    setDrawingMode('polygon');
    setFormData(prev => ({ ...prev, boundaries: null }));
  }, []);

  // Complete polygon manually via button
  const handleCompletePolygon = useCallback(() => {
    if (customDrawingPoints.length < 3) return;

    const coordinates = customDrawingPoints.map(p => [p.lng, p.lat]);
    coordinates.push(coordinates[0]); // Close polygon

    setFormData(prev => ({
      ...prev,
      boundaries: {
        type: 'Polygon',
        coordinates: [coordinates]
      }
    }));

    const snappedPoints = customDrawingPoints.filter(p => p.snapped).length;
    setSnappedCount(snappedPoints);

    setIsCustomDrawing(false);
    setCustomDrawingPoints([]);
    setSnapIndicator(null);
    setDrawingMode(null);

    if (snappedPoints > 0) {
      alert(`✅ Polygon completed! ${snappedPoints} point(s) snapped to existing corners.`);
    }
  }, [customDrawingPoints]);

  // ============ BATCH CUSTOM DRAWING HANDLERS ============

  const handleBatchMapClickForDrawing = useCallback((event) => {
    if (!isBatchCustomDrawing) return;

    const clickLat = event.latLng.lat();
    const clickLng = event.latLng.lng();

    // Check if clicking near first point to close polygon
    if (batchCustomDrawingPoints.length >= 3) {
      const firstPoint = batchCustomDrawingPoints[0];
      const distToFirst = getDistanceInMeters(clickLat, clickLng, firstPoint.lat, firstPoint.lng);

      if (distToFirst < 50) { // Within 50 meters of first point = close polygon
        // Complete the polygon
        const coordinates = batchCustomDrawingPoints.map(p => [p.lng, p.lat]);
        coordinates.push(coordinates[0]);

        setBatchWorkAreas(prev => {
          const updated = [...prev];
          updated[currentBatchIndex] = {
            ...updated[currentBatchIndex],
            boundaries: {
              type: 'Polygon',
              coordinates: [coordinates]
            }
          };
          return updated;
        });

        const snappedPoints = batchCustomDrawingPoints.filter(p => p.snapped).length;
        if (snappedPoints > 0) {
          console.log(`✅ ${snappedPoints} point(s) snapped for ${batchWorkAreas[currentBatchIndex]?.name}`);
        }

        setIsBatchCustomDrawing(false);
        setBatchCustomDrawingPoints([]);
        setBatchSnapIndicator(null);

        // Auto-advance to next
        setTimeout(() => {
          if (currentBatchIndex < batchWorkAreas.length - 1) {
            setCurrentBatchIndex(prev => prev + 1);
          }
        }, 500);
        return;
      }
    }

    // Check for snap - include already drawn batch areas
    const drawnBatchAreas = batchWorkAreas
      .filter((wa, idx) => idx !== currentBatchIndex && wa.boundaries?.coordinates?.[0])
      .map(wa => ({ _id: `batch-${wa.name}`, name: wa.name, boundaries: wa.boundaries }));

    const snapResult = findNearestSnapPoint(clickLat, clickLng, null, drawnBatchAreas);

    let newPoint;
    if (snapResult) {
      newPoint = {
        lat: snapResult.lat,
        lng: snapResult.lng,
        snapped: true,
        snapType: snapResult.type,
        snapArea: snapResult.areaName
      };
      console.log(`🧲 BATCH SNAPPED to ${snapResult.type} of "${snapResult.areaName}"`);
    } else {
      newPoint = { lat: clickLat, lng: clickLng, snapped: false };
    }

    setBatchCustomDrawingPoints(prev => [...prev, newPoint]);
  }, [isBatchCustomDrawing, batchCustomDrawingPoints, currentBatchIndex, batchWorkAreas, findNearestSnapPoint, getDistanceInMeters]);

  const handleBatchMapMouseMove = useCallback((event) => {
    if (!isBatchCustomDrawing) {
      setBatchSnapIndicator(null);
      return;
    }

    const lat = event.latLng.lat();
    const lng = event.latLng.lng();

    const drawnBatchAreas = batchWorkAreas
      .filter((wa, idx) => idx !== currentBatchIndex && wa.boundaries?.coordinates?.[0])
      .map(wa => ({ _id: `batch-${wa.name}`, name: wa.name, boundaries: wa.boundaries }));

    const snapResult = findNearestSnapPoint(lat, lng, null, drawnBatchAreas);

    if (snapResult) {
      setBatchSnapIndicator({
        lat: snapResult.lat,
        lng: snapResult.lng,
        type: snapResult.type,
        areaName: snapResult.areaName
      });
    } else {
      setBatchSnapIndicator(null);
    }
  }, [isBatchCustomDrawing, currentBatchIndex, batchWorkAreas, findNearestSnapPoint]);

  const handleBatchMapDoubleClick = useCallback((event) => {
    if (!isBatchCustomDrawing || batchCustomDrawingPoints.length < 3) return;

    const coordinates = batchCustomDrawingPoints.map(p => [p.lng, p.lat]);
    coordinates.push(coordinates[0]);

    setBatchWorkAreas(prev => {
      const updated = [...prev];
      updated[currentBatchIndex] = {
        ...updated[currentBatchIndex],
        boundaries: {
          type: 'Polygon',
          coordinates: [coordinates]
        }
      };
      return updated;
    });

    setIsBatchCustomDrawing(false);
    setBatchCustomDrawingPoints([]);
    setBatchSnapIndicator(null);

    setTimeout(() => {
      if (currentBatchIndex < batchWorkAreas.length - 1) {
        setCurrentBatchIndex(prev => prev + 1);
      }
    }, 500);
  }, [isBatchCustomDrawing, batchCustomDrawingPoints, currentBatchIndex, batchWorkAreas]);

  const startBatchCustomDrawing = useCallback(() => {
    setIsBatchCustomDrawing(true);
    setBatchCustomDrawingPoints([]);
    setBatchSnapIndicator(null);
  }, []);

  // Complete batch polygon manually via button
  const handleCompleteBatchPolygon = useCallback(() => {
    if (batchCustomDrawingPoints.length < 3) return;

    const coordinates = batchCustomDrawingPoints.map(p => [p.lng, p.lat]);
    coordinates.push(coordinates[0]);

    setBatchWorkAreas(prev => {
      const updated = [...prev];
      updated[currentBatchIndex] = {
        ...updated[currentBatchIndex],
        boundaries: {
          type: 'Polygon',
          coordinates: [coordinates]
        }
      };
      return updated;
    });

    const snappedPoints = batchCustomDrawingPoints.filter(p => p.snapped).length;
    if (snappedPoints > 0) {
      console.log(`✅ ${snappedPoints} point(s) snapped for ${batchWorkAreas[currentBatchIndex]?.name}`);
    }

    setIsBatchCustomDrawing(false);
    setBatchCustomDrawingPoints([]);
    setBatchSnapIndicator(null);

    // Auto-advance to next
    setTimeout(() => {
      if (currentBatchIndex < batchWorkAreas.length - 1) {
        setCurrentBatchIndex(prev => prev + 1);
      }
    }, 500);
  }, [batchCustomDrawingPoints, currentBatchIndex, batchWorkAreas]);

  // ============ END SNAP FUNCTIONS ============

  // Handle polygon completion from DrawingManager - auto-snap after drawing (FALLBACK)
  const onPolygonComplete = useCallback((polygon) => {
    const path = polygon.getPath();
    const coordinates = [];

    for (let i = 0; i < path.getLength(); i++) {
      const point = path.getAt(i);
      coordinates.push([point.lng(), point.lat()]);
    }

    if (coordinates.length > 0) {
      coordinates.push(coordinates[0]); // Close the polygon
    }

    // Auto-snap points that are close to existing edges
    const { coords: snappedCoords, snappedCount: count } = snapCoordinatesToEdges(coordinates, editingArea?._id);

    setFormData(prev => ({
      ...prev,
      boundaries: {
        type: 'Polygon',
        coordinates: [snappedCoords]
      }
    }));

    setSnappedCount(count);
    setCurrentPolygon(polygon);
    setDrawingMode(null);
    polygon.setMap(null); // Remove the drawn polygon (we show our own)

    // Show feedback to user
    if (count > 0) {
      console.log(`Auto-snapped ${count} point(s) to existing edges`);
      alert(`✅ ${count} point(s) auto-snapped to nearby existing area edges!`);
    } else {
      const existingAreas = workAreas.filter(a => a._id?.toString() !== editingArea?._id?.toString() && a.boundaries?.coordinates?.[0]);
      if (existingAreas.length === 0) {
        console.log('No existing areas to snap to');
        alert('ℹ️ No existing work areas found to snap to. Your polygon was saved as-is.');
      } else {
        console.log(`No points within ${SNAP_THRESHOLD_METERS}m of existing edges`);
        alert(`ℹ️ No points were within ${SNAP_THRESHOLD_METERS}m of existing edges. Draw closer to existing areas to snap.`);
      }
    }
  }, [snapCoordinatesToEdges, editingArea]);

  // Batch polygon complete handler - auto-snap after drawing
  const onBatchPolygonComplete = useCallback((polygon) => {
    const path = polygon.getPath();
    const coordinates = [];

    for (let i = 0; i < path.getLength(); i++) {
      const point = path.getAt(i);
      coordinates.push([point.lng(), point.lat()]);
    }

    if (coordinates.length > 0) {
      coordinates.push(coordinates[0]); // Close the polygon
    }

    // Include already-drawn batch areas for snapping
    const drawnBatchAreas = batchWorkAreas
      .filter((wa, idx) => idx !== currentBatchIndex && wa.boundaries?.coordinates?.[0])
      .map(wa => ({ _id: `batch-${wa.name}`, boundaries: wa.boundaries }));

    // Auto-snap points to existing edges
    const { coords: snappedCoords, snappedCount: count } = snapCoordinatesToEdges(coordinates, null, drawnBatchAreas);

    if (count > 0) {
      console.log(`Auto-snapped ${count} point(s) to existing edges`);
    }

    // Update the current work area's boundaries with snapped coordinates
    setBatchWorkAreas(prev => {
      const updated = [...prev];
      updated[currentBatchIndex] = {
        ...updated[currentBatchIndex],
        boundaries: {
          type: 'Polygon',
          coordinates: [snappedCoords]
        }
      };
      return updated;
    });

    setBatchDrawingMode(false);
    polygon.setMap(null);

    // Auto-advance to next work area after a short delay
    setTimeout(() => {
      if (currentBatchIndex < batchWorkAreas.length - 1) {
        setCurrentBatchIndex(prev => prev + 1);
      }
    }, 500);
  }, [currentBatchIndex, batchWorkAreas, snapCoordinatesToEdges]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.boundaries || !formData.boundaries.coordinates) {
      alert('Please draw a boundary on the map for this work area');
      return;
    }

    try {
      if (editingArea) {
        await workAreaAPI.updateWorkArea(editingArea._id, formData);
        alert('Work area updated successfully');
      } else {
        await workAreaAPI.createWorkArea(formData);
        alert('Work area added successfully');
      }
      setShowModal(false);
      setFormData({ name: '', city: '', state: '', description: '', isActive: true, boundaries: null });
      setEditingArea(null);
      setCurrentPolygon(null);
      setSnappedCount(0);
      fetchWorkAreas();
    } catch (error) {
      alert(error.response?.data?.message || 'Failed to save work area');
    }
  };

  const handleEdit = (area) => {
    setEditingArea(area);
    setFormData({
      name: area.name,
      city: area.city,
      state: area.state,
      description: area.description || '',
      isActive: area.isActive,
      boundaries: area.boundaries
    });

    // Center map on the work area
    if (area.boundaries && area.boundaries.coordinates && area.boundaries.coordinates[0]) {
      const coords = area.boundaries.coordinates[0];
      if (coords.length > 0) {
        setMapCenter({ lat: coords[0][1], lng: coords[0][0] });
      }
    }

    setShowModal(true);
  };

  const handleAddArea = () => {
    setShowModal(true);
    setFormData({ name: '', city: '', state: '', description: '', isActive: true, boundaries: null });
    setEditingArea(null);
    setCurrentPolygon(null);
    setSnappedCount(0);
  };

  const handleToggleStatus = async (areaId) => {
    try {
      await workAreaAPI.toggleWorkAreaStatus(areaId);
      fetchWorkAreas();
    } catch (error) {
      alert('Failed to toggle work area status');
    }
  };

  const handleDelete = async (areaId) => {
    if (!window.confirm('Are you sure you want to delete this work area?')) return;

    try {
      await workAreaAPI.deleteWorkArea(areaId);
      alert('Work area deleted successfully');
      fetchWorkAreas();
    } catch (error) {
      alert(error.response?.data?.message || 'Failed to delete work area');
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingArea(null);
    setFormData({ name: '', city: '', state: '', description: '', isActive: true, boundaries: null });
    setCurrentPolygon(null);
    setDrawingMode(null);
    setSnappedCount(0);
    // Reset custom drawing state
    setIsCustomDrawing(false);
    setCustomDrawingPoints([]);
    setSnapIndicator(null);
    setLastSnapInfo(null);
  };

  const handleCityChange = (cityName) => {
    const selectedCity = cities.find(c => c.name === cityName);
    setFormData({
      ...formData,
      city: cityName,
      state: selectedCity ? selectedCity.state : ''
    });

    // Center map on selected city
    if (cityCoordinates[cityName]) {
      setMapCenter(cityCoordinates[cityName]);
    }
  };

  const handleClearPolygon = () => {
    setFormData(prev => ({
      ...prev,
      boundaries: null
    }));
    setCurrentPolygon(null);
    setDrawingMode(null);
    setSnappedCount(0);
    // Reset custom drawing state
    setIsCustomDrawing(false);
    setCustomDrawingPoints([]);
    setSnapIndicator(null);
    setLastSnapInfo(null);
  };

  const getPolygonPaths = (boundaries) => {
    if (!boundaries || !boundaries.coordinates || !boundaries.coordinates[0]) return [];
    return boundaries.coordinates[0].map(coord => ({
      lat: coord[1],
      lng: coord[0]
    }));
  };

  const getSurgeColor = (surge) => {
    if (surge >= 1.4) return '#ef4444'; // red
    if (surge >= 1.2) return '#f59e0b'; // orange
    if (surge >= 1.1) return '#eab308'; // yellow
    return '#10b981'; // green
  };

  // Batch creation handlers
  const handleOpenBatchModal = () => {
    setShowBatchModal(true);
    setBatchCity('');
    setBatchState('');
    setBatchWorkAreas([]);
    setNewWorkAreaName('');
    setNewWorkAreaDescription('');
    setCurrentBatchIndex(-1);
    setBatchDrawingMode(false);
  };

  const handleCloseBatchModal = () => {
    setShowBatchModal(false);
    setBatchCity('');
    setBatchState('');
    setBatchWorkAreas([]);
    setCurrentBatchIndex(-1);
    setBatchDrawingMode(false);
    // Reset batch custom drawing state
    setIsBatchCustomDrawing(false);
    setBatchCustomDrawingPoints([]);
    setBatchSnapIndicator(null);
  };

  const handleBatchCityChange = (cityName) => {
    const selectedCity = cities.find(c => c.name === cityName);
    setBatchCity(cityName);
    setBatchState(selectedCity ? selectedCity.state : '');

    // Center map on selected city
    if (cityCoordinates[cityName]) {
      setMapCenter(cityCoordinates[cityName]);
    }
  };

  const handleAddWorkAreaToList = () => {
    if (!newWorkAreaName.trim()) {
      alert('Please enter a work area name');
      return;
    }

    // Check for duplicates
    if (batchWorkAreas.some(wa => wa.name.toLowerCase() === newWorkAreaName.trim().toLowerCase())) {
      alert('This work area name already exists in the list');
      return;
    }

    setBatchWorkAreas(prev => [...prev, {
      name: newWorkAreaName.trim(),
      description: newWorkAreaDescription.trim(),
      boundaries: null
    }]);

    setNewWorkAreaName('');
    setNewWorkAreaDescription('');
  };

  const handleRemoveWorkAreaFromList = (index) => {
    setBatchWorkAreas(prev => prev.filter((_, i) => i !== index));
  };

  const handleStartDrawing = () => {
    if (batchWorkAreas.length === 0) {
      alert('Please add at least one work area to the list');
      return;
    }
    setCurrentBatchIndex(0);
    setBatchDrawingMode(true);
  };

  const handleClearBatchPolygon = (index) => {
    setBatchWorkAreas(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], boundaries: null };
      return updated;
    });
    setBatchDrawingMode(false);
    // Reset batch custom drawing state
    setIsBatchCustomDrawing(false);
    setBatchCustomDrawingPoints([]);
    setBatchSnapIndicator(null);
  };

  const handleSaveBatchWorkAreas = async () => {
    // Validate all work areas have boundaries
    const missingBoundaries = batchWorkAreas.filter(wa => !wa.boundaries);
    if (missingBoundaries.length > 0) {
      alert(`Please draw boundaries for: ${missingBoundaries.map(wa => wa.name).join(', ')}`);
      return;
    }

    setSavingBatch(true);
    let successCount = 0;
    let errorCount = 0;

    for (const workArea of batchWorkAreas) {
      try {
        await workAreaAPI.createWorkArea({
          name: workArea.name,
          description: workArea.description,
          city: batchCity,
          state: batchState,
          isActive: true,
          boundaries: workArea.boundaries
        });
        successCount++;
      } catch (error) {
        console.error(`Failed to create work area: ${workArea.name}`, error);
        errorCount++;
      }
    }

    setSavingBatch(false);

    if (errorCount > 0) {
      alert(`Created ${successCount} work areas. ${errorCount} failed.`);
    } else {
      alert(`Successfully created ${successCount} work areas!`);
    }

    handleCloseBatchModal();
    fetchWorkAreas();
  };

  // Get polygon color based on state
  const getBatchPolygonColor = (index) => {
    if (index === currentBatchIndex) return '#2196F3'; // Blue - currently drawing
    if (batchWorkAreas[index]?.boundaries) return '#10b981'; // Green - completed
    return '#9ca3af'; // Gray - pending
  };

  return (
    <div style={{ background: 'linear-gradient(135deg, #f5f7fa 0%, #e8eef3 100%)', minHeight: '100vh' }}>
      <Navbar />
      <div className="container" style={{ padding: 'var(--space-lg)', maxWidth: '1400px', margin: '0 auto' }}>
        {/* Header */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 'var(--space-lg)',
          flexWrap: 'wrap',
          gap: 'var(--space-md)'
        }}>
          <h1 style={{
            fontSize: 'var(--font-size-2xl)',
            fontWeight: 'var(--font-weight-bold)',
            color: 'var(--color-text-primary)'
          }}>
            📍 Work Area Management
          </h1>
          <div style={{ display: 'flex', gap: 'var(--space-md)' }}>
            <button
              onClick={handleOpenBatchModal}
              className="btn"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 'var(--space-sm)',
                background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
                color: 'white',
                border: 'none',
                padding: '12px 24px',
                borderRadius: '8px',
                fontWeight: '600',
                cursor: 'pointer'
              }}
            >
              <span>🗺️</span>
              <span>Batch Create (Multi-Area)</span>
            </button>
            <button
              onClick={handleAddArea}
              className="btn btn-primary"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 'var(--space-sm)'
              }}
            >
              <span>➕</span>
              <span>Add Single Area</span>
            </button>
          </div>
        </div>

        {/* Filters */}
        <div style={{
          display: 'flex',
          gap: 'var(--space-md)',
          marginBottom: 'var(--space-lg)',
          flexWrap: 'wrap'
        }}>
          <input
            type="text"
            placeholder="Search work areas..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{
              flex: 1,
              minWidth: '250px',
              padding: 'var(--space-md)',
              borderRadius: 'var(--radius-md)',
              border: '1px solid var(--color-border)',
              fontSize: 'var(--font-size-md)'
            }}
          />
          <select
            value={filterCity}
            onChange={(e) => setFilterCity(e.target.value)}
            style={{
              padding: 'var(--space-md)',
              borderRadius: 'var(--radius-md)',
              border: '1px solid var(--color-border)',
              fontSize: 'var(--font-size-md)',
              minWidth: '200px'
            }}
          >
            <option value="">All Cities</option>
            {cities.map((city, index) => (
              <option key={index} value={city.name}>{city.name}</option>
            ))}
          </select>
        </div>

        {/* Work Areas Table */}
        {loading ? (
          <div style={{ textAlign: 'center', padding: 'var(--space-2xl)' }}>
            <div className="loading-spinner"></div>
          </div>
        ) : workAreas.length === 0 ? (
          <div className="card" style={{
            textAlign: 'center',
            padding: 'var(--space-2xl)'
          }}>
            <p style={{
              fontSize: 'var(--font-size-lg)',
              color: 'var(--color-text-secondary)'
            }}>
              No work areas found
            </p>
          </div>
        ) : (
          <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
            <table style={{
              width: '100%',
              borderCollapse: 'collapse'
            }}>
              <thead>
                <tr style={{
                  background: 'var(--color-background-secondary)',
                  borderBottom: '2px solid var(--color-border)'
                }}>
                  <th style={{ padding: 'var(--space-md)', textAlign: 'left' }}>Work Area</th>
                  <th style={{ padding: 'var(--space-md)', textAlign: 'left' }}>City</th>
                  <th style={{ padding: 'var(--space-md)', textAlign: 'left' }}>State</th>
                  <th style={{ padding: 'var(--space-md)', textAlign: 'center' }}>Surge</th>
                  <th style={{ padding: 'var(--space-md)', textAlign: 'center' }}>Status</th>
                  <th style={{ padding: 'var(--space-md)', textAlign: 'center' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {workAreas.map((area) => (
                  <tr key={area._id} style={{
                    borderBottom: '1px solid var(--color-border)'
                  }}>
                    <td style={{ padding: 'var(--space-md)' }}>
                      <div>
                        <span style={{ fontWeight: '600', display: 'block' }}>{area.name}</span>
                        {area.description && (
                          <span style={{
                            fontSize: 'var(--font-size-sm)',
                            color: 'var(--color-text-secondary)',
                            display: 'block',
                            marginTop: '4px'
                          }}>
                            {area.description.substring(0, 50)}{area.description.length > 50 ? '...' : ''}
                          </span>
                        )}
                      </div>
                    </td>
                    <td style={{ padding: 'var(--space-md)' }}>
                      {area.city}
                    </td>
                    <td style={{ padding: 'var(--space-md)' }}>
                      {area.state}
                    </td>
                    <td style={{ padding: 'var(--space-md)', textAlign: 'center' }}>
                      <span style={{
                        padding: 'var(--space-xs) var(--space-md)',
                        borderRadius: 'var(--radius-full)',
                        fontSize: 'var(--font-size-sm)',
                        fontWeight: '600',
                        background: getSurgeColor(area.currentSurgeMultiplier || 1.0),
                        color: 'white'
                      }}>
                        {(area.currentSurgeMultiplier || 1.0).toFixed(1)}x
                      </span>
                    </td>
                    <td style={{ padding: 'var(--space-md)', textAlign: 'center' }}>
                      <span style={{
                        padding: 'var(--space-xs) var(--space-md)',
                        borderRadius: 'var(--radius-full)',
                        fontSize: 'var(--font-size-sm)',
                        fontWeight: '600',
                        background: area.isActive
                          ? 'linear-gradient(135deg, #10b981 0%, #059669 100%)'
                          : 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                        color: 'white'
                      }}>
                        {area.isActive ? '✓ Active' : '✗ Inactive'}
                      </span>
                    </td>
                    <td style={{ padding: 'var(--space-md)' }}>
                      <div style={{
                        display: 'flex',
                        gap: 'var(--space-sm)',
                        justifyContent: 'center'
                      }}>
                        <button
                          onClick={() => handleEdit(area)}
                          className="btn btn-primary"
                          style={{
                            padding: 'var(--space-sm) var(--space-md)',
                            fontSize: 'var(--font-size-sm)'
                          }}
                        >
                          ✏️ Edit
                        </button>
                        <button
                          onClick={() => handleToggleStatus(area._id)}
                          className={area.isActive ? 'btn btn-warning' : 'btn btn-success'}
                          style={{
                            padding: 'var(--space-sm) var(--space-md)',
                            fontSize: 'var(--font-size-sm)'
                          }}
                        >
                          {area.isActive ? '⏸️ Deactivate' : '▶️ Activate'}
                        </button>
                        <button
                          onClick={() => handleDelete(area._id)}
                          className="btn btn-danger"
                          style={{
                            padding: 'var(--space-sm) var(--space-md)',
                            fontSize: 'var(--font-size-sm)'
                          }}
                        >
                          🗑️ Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Single Area Modal */}
        {showModal && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: '20px'
          }}>
            <div className="card" style={{
              maxWidth: '900px',
              width: '100%',
              maxHeight: '90vh',
              overflow: 'auto'
            }}>
              <h2 style={{
                fontSize: 'var(--font-size-xl)',
                fontWeight: 'var(--font-weight-bold)',
                marginBottom: 'var(--space-lg)'
              }}>
                {editingArea ? 'Edit Work Area' : 'Add New Work Area'}
              </h2>

              <form onSubmit={handleSubmit}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-md)', marginBottom: 'var(--space-md)' }}>
                  <div>
                    <label style={{
                      display: 'block',
                      marginBottom: 'var(--space-sm)',
                      fontWeight: '600'
                    }}>
                      City *
                    </label>
                    <select
                      value={formData.city}
                      onChange={(e) => handleCityChange(e.target.value)}
                      required
                      style={{
                        width: '100%',
                        padding: 'var(--space-md)',
                        borderRadius: 'var(--radius-md)',
                        border: '1px solid var(--color-border)',
                        fontSize: 'var(--font-size-md)'
                      }}
                    >
                      <option value="">Select City</option>
                      {cities.map((city, index) => (
                        <option key={index} value={city.name}>{city.name}, {city.state}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label style={{
                      display: 'block',
                      marginBottom: 'var(--space-sm)',
                      fontWeight: '600'
                    }}>
                      Work Area Name *
                    </label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      placeholder="e.g., Andheri West, Bandra East"
                      required
                      style={{
                        width: '100%',
                        padding: 'var(--space-md)',
                        borderRadius: 'var(--radius-md)',
                        border: '1px solid var(--color-border)',
                        fontSize: 'var(--font-size-md)'
                      }}
                    />
                  </div>
                </div>

                <div style={{ marginBottom: 'var(--space-md)' }}>
                  <label style={{
                    display: 'block',
                    marginBottom: 'var(--space-sm)',
                    fontWeight: '600'
                  }}>
                    Description
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Brief description of this work area (optional)"
                    rows="2"
                    style={{
                      width: '100%',
                      padding: 'var(--space-md)',
                      borderRadius: 'var(--radius-md)',
                      border: '1px solid var(--color-border)',
                      fontSize: 'var(--font-size-md)',
                      fontFamily: 'inherit',
                      resize: 'vertical'
                    }}
                  />
                </div>

                {/* Google Maps Drawing Section */}
                <div style={{ marginBottom: 'var(--space-md)' }}>
                  <label style={{
                    display: 'block',
                    marginBottom: 'var(--space-sm)',
                    fontWeight: '600'
                  }}>
                    Work Area Boundary *
                    {formData.boundaries && <span style={{ color: '#10b981', marginLeft: '8px' }}>✓ Boundary drawn</span>}
                    {snappedCount > 0 && <span style={{ color: '#f59e0b', marginLeft: '8px' }}>🧲 {snappedCount} points auto-snapped</span>}
                  </label>

                  <div style={{ marginBottom: 'var(--space-sm)', display: 'flex', gap: 'var(--space-sm)', flexWrap: 'wrap', alignItems: 'center' }}>
                    {!formData.boundaries && !isCustomDrawing && (
                      <button
                        type="button"
                        onClick={startCustomDrawing}
                        className="btn btn-primary"
                        style={{ fontSize: 'var(--font-size-sm)' }}
                      >
                        🖊️ Draw Boundary
                      </button>
                    )}
                    {formData.boundaries && (
                      <button
                        type="button"
                        onClick={handleClearPolygon}
                        className="btn btn-danger"
                        style={{ fontSize: 'var(--font-size-sm)' }}
                      >
                        🗑️ Clear & Redraw
                      </button>
                    )}
                    {isCustomDrawing && (
                      <>
                        <span style={{ fontSize: 'var(--font-size-sm)', color: '#2196F3', fontWeight: '600' }}>
                          Points: {customDrawingPoints.length}
                        </span>
                        {customDrawingPoints.length >= 3 && (
                          <button
                            type="button"
                            onClick={handleCompletePolygon}
                            className="btn btn-success"
                            style={{
                              fontSize: 'var(--font-size-sm)',
                              background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                              color: 'white',
                              border: 'none'
                            }}
                          >
                            ✓ Complete Polygon
                          </button>
                        )}
                        <button
                          type="button"
                          onClick={() => {
                            setIsCustomDrawing(false);
                            setCustomDrawingPoints([]);
                            setSnapIndicator(null);
                          }}
                          className="btn btn-secondary"
                          style={{ fontSize: 'var(--font-size-sm)' }}
                        >
                          ✕ Cancel
                        </button>
                        {snapIndicator && (
                          <span style={{
                            fontSize: 'var(--font-size-sm)',
                            color: '#f59e0b',
                            fontWeight: '600',
                            background: '#fef3c7',
                            padding: '2px 8px',
                            borderRadius: '4px'
                          }}>
                            🧲 Snap to {snapIndicator.type} of "{snapIndicator.areaName}"
                          </span>
                        )}
                      </>
                    )}
                  </div>

                  {/* Real-time snap info */}
                  <div style={{
                    background: '#dbeafe',
                    padding: 'var(--space-sm) var(--space-md)',
                    borderRadius: 'var(--radius-md)',
                    marginBottom: 'var(--space-sm)',
                    fontSize: 'var(--font-size-sm)',
                    border: '1px solid #93c5fd'
                  }}>
                    <strong>🧲 Corner Snap ({SNAP_THRESHOLD_METERS}m):</strong> Click points to draw. When near an existing area's <strong>corner</strong>, you'll see a <span style={{ color: '#f59e0b', fontWeight: 'bold' }}>yellow snap indicator</span>. Click to snap! Double-click or click near first point to complete.
                  </div>

                  <LoadScript googleMapsApiKey={GOOGLE_MAPS_API_KEY} libraries={libraries}>
                    <GoogleMap
                      mapContainerStyle={mapContainerStyle}
                      center={mapCenter}
                      zoom={13}
                      onLoad={map => { mapRef.current = map }}
                      onClick={handleMapClickForDrawing}
                      onMouseMove={handleMapMouseMove}
                      onDblClick={handleMapDoubleClick}
                      options={{
                        disableDoubleClickZoom: isCustomDrawing // Disable zoom on double-click when drawing
                      }}
                    >
                      {/* Display EXISTING work areas (so admin can see where to draw edge-to-edge) */}
                      {workAreas
                        .filter(area => area._id !== editingArea?._id && area.boundaries?.coordinates)
                        .map((area) => (
                          <Polygon
                            key={area._id}
                            paths={getPolygonPaths(area.boundaries)}
                            options={{
                              fillColor: '#6b7280',
                              fillOpacity: 0.25,
                              strokeColor: '#6b7280',
                              strokeOpacity: 0.8,
                              strokeWeight: 2
                            }}
                          />
                        ))}

                      {/* Display current boundary (completed polygon) */}
                      {formData.boundaries && (
                        <Polygon
                          paths={getPolygonPaths(formData.boundaries)}
                          options={{
                            fillColor: '#10b981',
                            fillOpacity: 0.35,
                            strokeColor: '#10b981',
                            strokeOpacity: 1,
                            strokeWeight: 2
                          }}
                        />
                      )}

                      {/* Custom drawing: Polyline connecting points being drawn */}
                      {isCustomDrawing && customDrawingPoints.length > 0 && (
                        <Polyline
                          path={customDrawingPoints.map(p => ({ lat: p.lat, lng: p.lng }))}
                          options={{
                            strokeColor: '#2196F3',
                            strokeWeight: 3,
                            strokeOpacity: 1
                          }}
                        />
                      )}

                      {/* Custom drawing: Markers for each point */}
                      {isCustomDrawing && customDrawingPoints.map((point, index) => (
                        <Marker
                          key={index}
                          position={{ lat: point.lat, lng: point.lng }}
                          clickable={false}
                          icon={{
                            path: window.google?.maps?.SymbolPath?.CIRCLE,
                            scale: index === 0 ? 10 : 7,
                            fillColor: point.snapped ? '#f59e0b' : '#2196F3',
                            fillOpacity: 1,
                            strokeColor: '#ffffff',
                            strokeWeight: 2
                          }}
                          title={point.snapped ? `Snapped to ${point.snapArea}` : `Point ${index + 1}`}
                        />
                      ))}

                      {/* Snap indicator: Yellow circle showing where point will snap to */}
                      {isCustomDrawing && snapIndicator && (
                        <Circle
                          center={{ lat: snapIndicator.lat, lng: snapIndicator.lng }}
                          radius={15}
                          options={{
                            fillColor: '#f59e0b',
                            fillOpacity: 0.5,
                            strokeColor: '#f59e0b',
                            strokeWeight: 3,
                            strokeOpacity: 1,
                            clickable: false,
                            zIndex: 1
                          }}
                        />
                      )}

                      {/* Visual indicator for first point (green circle) */}
                      {isCustomDrawing && customDrawingPoints.length >= 3 && (
                        <Circle
                          center={{ lat: customDrawingPoints[0].lat, lng: customDrawingPoints[0].lng }}
                          radius={50}
                          options={{
                            fillColor: '#10b981',
                            fillOpacity: 0.4,
                            strokeColor: '#10b981',
                            strokeWeight: 3,
                            strokeOpacity: 1,
                            clickable: false,
                            zIndex: 1
                          }}
                        />
                      )}
                    </GoogleMap>
                  </LoadScript>

                  <div style={{
                    marginTop: 'var(--space-sm)',
                    fontSize: 'var(--font-size-sm)',
                    color: 'var(--color-text-secondary)'
                  }}>
                    <p style={{ margin: '0 0 var(--space-xs) 0' }}>
                      Click "Draw Boundary" then click points on map. <strong>Move cursor near gray areas to see snap indicator!</strong> Double-click or click near first point (green circle) to complete.
                    </p>
                    <div style={{ display: 'flex', gap: 'var(--space-md)', flexWrap: 'wrap' }}>
                      <span><span style={{ color: '#10b981' }}>■</span> Completed Area</span>
                      <span><span style={{ color: '#2196F3' }}>●</span> Drawing Points</span>
                      <span><span style={{ color: '#f59e0b' }}>●</span> Snapped Points</span>
                      <span><span style={{ color: '#6b7280' }}>■</span> Existing Areas</span>
                    </div>
                  </div>
                </div>

                <div style={{ marginBottom: 'var(--space-lg)' }}>
                  <label style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 'var(--space-sm)',
                    cursor: 'pointer'
                  }}>
                    <input
                      type="checkbox"
                      checked={formData.isActive}
                      onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                      style={{
                        width: '18px',
                        height: '18px',
                        cursor: 'pointer'
                      }}
                    />
                    <span style={{ fontWeight: '600' }}>Active (Available for delivery partners)</span>
                  </label>
                </div>

                <div style={{
                  display: 'flex',
                  gap: 'var(--space-md)',
                  justifyContent: 'flex-end'
                }}>
                  <button
                    type="button"
                    onClick={handleCloseModal}
                    className="btn btn-secondary"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn btn-primary"
                  >
                    {editingArea ? 'Update Work Area' : 'Add Work Area'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Batch Create Modal */}
        {showBatchModal && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: '20px'
          }}>
            <div className="card" style={{
              maxWidth: '1200px',
              width: '100%',
              maxHeight: '95vh',
              overflow: 'auto'
            }}>
              <h2 style={{
                fontSize: 'var(--font-size-xl)',
                fontWeight: 'var(--font-weight-bold)',
                marginBottom: 'var(--space-md)',
                display: 'flex',
                alignItems: 'center',
                gap: 'var(--space-sm)'
              }}>
                🗺️ Batch Create Work Areas
                <span style={{
                  fontSize: 'var(--font-size-sm)',
                  fontWeight: 'normal',
                  color: 'var(--color-text-secondary)',
                  marginLeft: 'auto'
                }}>
                  {currentBatchIndex === -1
                    ? 'Step 1: Define work areas'
                    : `Step 2: Drawing boundaries (${batchWorkAreas.filter(wa => wa.boundaries).length}/${batchWorkAreas.length})`}
                </span>
              </h2>

              <div style={{ display: 'grid', gridTemplateColumns: currentBatchIndex === -1 ? '1fr' : 'minmax(280px, 350px) 1fr', gap: 'var(--space-lg)' }}>
                {/* Left Panel - Work Area List */}
                <div>
                  {/* City Selection */}
                  <div style={{ marginBottom: 'var(--space-md)' }}>
                    <label style={{ display: 'block', marginBottom: 'var(--space-sm)', fontWeight: '600' }}>
                      Select City *
                    </label>
                    <select
                      value={batchCity}
                      onChange={(e) => handleBatchCityChange(e.target.value)}
                      disabled={currentBatchIndex !== -1}
                      style={{
                        width: '100%',
                        padding: 'var(--space-md)',
                        borderRadius: 'var(--radius-md)',
                        border: '1px solid var(--color-border)',
                        fontSize: 'var(--font-size-md)',
                        background: currentBatchIndex !== -1 ? '#f3f4f6' : 'white'
                      }}
                    >
                      <option value="">Select City</option>
                      {cities.map((city, index) => (
                        <option key={index} value={city.name}>{city.name}, {city.state}</option>
                      ))}
                    </select>
                  </div>

                  {/* Add Work Area Form */}
                  {currentBatchIndex === -1 && batchCity && (
                    <div style={{
                      background: '#f8fafc',
                      padding: 'var(--space-md)',
                      borderRadius: 'var(--radius-md)',
                      marginBottom: 'var(--space-md)'
                    }}>
                      <label style={{ display: 'block', marginBottom: 'var(--space-sm)', fontWeight: '600' }}>
                        Add Work Area to List
                      </label>
                      <input
                        type="text"
                        value={newWorkAreaName}
                        onChange={(e) => setNewWorkAreaName(e.target.value)}
                        placeholder="Work area name (e.g., Sadar Bazaar)"
                        onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddWorkAreaToList())}
                        style={{
                          width: '100%',
                          padding: 'var(--space-md)',
                          borderRadius: 'var(--radius-md)',
                          border: '1px solid var(--color-border)',
                          fontSize: 'var(--font-size-md)',
                          marginBottom: 'var(--space-sm)'
                        }}
                      />
                      <input
                        type="text"
                        value={newWorkAreaDescription}
                        onChange={(e) => setNewWorkAreaDescription(e.target.value)}
                        placeholder="Description (optional)"
                        style={{
                          width: '100%',
                          padding: 'var(--space-sm)',
                          borderRadius: 'var(--radius-md)',
                          border: '1px solid var(--color-border)',
                          fontSize: 'var(--font-size-sm)',
                          marginBottom: 'var(--space-sm)'
                        }}
                      />
                      <button
                        type="button"
                        onClick={handleAddWorkAreaToList}
                        className="btn btn-primary"
                        style={{ width: '100%' }}
                      >
                        ➕ Add to List
                      </button>
                    </div>
                  )}

                  {/* Work Areas List */}
                  <div style={{ marginBottom: 'var(--space-md)' }}>
                    <label style={{ display: 'block', marginBottom: 'var(--space-sm)', fontWeight: '600' }}>
                      Work Areas List ({batchWorkAreas.length})
                    </label>
                    {batchWorkAreas.length === 0 ? (
                      <p style={{ color: 'var(--color-text-secondary)', fontSize: 'var(--font-size-sm)' }}>
                        No work areas added yet. Add work area names above.
                      </p>
                    ) : (
                      <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
                        {batchWorkAreas.map((wa, index) => (
                          <div
                            key={index}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 'var(--space-sm)',
                              padding: 'var(--space-sm) var(--space-md)',
                              marginBottom: 'var(--space-xs)',
                              borderRadius: 'var(--radius-md)',
                              background: index === currentBatchIndex
                                ? 'linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%)'
                                : wa.boundaries
                                  ? 'linear-gradient(135deg, #dcfce7 0%, #bbf7d0 100%)'
                                  : '#f8fafc',
                              border: index === currentBatchIndex ? '2px solid #2196F3' : '1px solid #e5e7eb'
                            }}
                          >
                            <span style={{
                              width: '24px',
                              height: '24px',
                              borderRadius: '50%',
                              background: getBatchPolygonColor(index),
                              color: 'white',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontSize: '12px',
                              fontWeight: '600'
                            }}>
                              {wa.boundaries ? '✓' : index + 1}
                            </span>
                            <div style={{ flex: 1 }}>
                              <span style={{ fontWeight: '600', display: 'block' }}>{wa.name}</span>
                              {wa.description && (
                                <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-secondary)' }}>
                                  {wa.description}
                                </span>
                              )}
                            </div>
                            {currentBatchIndex === -1 && (
                              <button
                                type="button"
                                onClick={() => handleRemoveWorkAreaFromList(index)}
                                style={{
                                  background: 'none',
                                  border: 'none',
                                  color: '#ef4444',
                                  cursor: 'pointer',
                                  padding: '4px'
                                }}
                              >
                                ✕
                              </button>
                            )}
                            {currentBatchIndex !== -1 && wa.boundaries && (
                              <button
                                type="button"
                                onClick={() => handleClearBatchPolygon(index)}
                                style={{
                                  background: '#ef4444',
                                  border: 'none',
                                  color: 'white',
                                  cursor: 'pointer',
                                  padding: '4px 8px',
                                  borderRadius: '4px',
                                  fontSize: '11px'
                                }}
                              >
                                Redraw
                              </button>
                            )}
                            {currentBatchIndex !== -1 && !wa.boundaries && index !== currentBatchIndex && (
                              <button
                                type="button"
                                onClick={() => {
                                  setCurrentBatchIndex(index);
                                  setBatchDrawingMode(true);
                                }}
                                style={{
                                  background: '#2196F3',
                                  border: 'none',
                                  color: 'white',
                                  cursor: 'pointer',
                                  padding: '4px 8px',
                                  borderRadius: '4px',
                                  fontSize: '11px'
                                }}
                              >
                                Draw
                              </button>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Action Buttons */}
                  {currentBatchIndex === -1 ? (
                    <button
                      type="button"
                      onClick={handleStartDrawing}
                      disabled={batchWorkAreas.length === 0 || !batchCity}
                      className="btn btn-primary"
                      style={{ width: '100%', marginBottom: 'var(--space-sm)' }}
                    >
                      🖊️ Start Drawing Boundaries →
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={handleSaveBatchWorkAreas}
                      disabled={savingBatch || batchWorkAreas.some(wa => !wa.boundaries)}
                      className="btn btn-success"
                      style={{
                        width: '100%',
                        marginBottom: 'var(--space-sm)',
                        background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                        color: 'white',
                        border: 'none'
                      }}
                    >
                      {savingBatch ? '💾 Saving...' : `💾 Save All ${batchWorkAreas.length} Work Areas`}
                    </button>
                  )}

                  <button
                    type="button"
                    onClick={handleCloseBatchModal}
                    className="btn btn-secondary"
                    style={{ width: '100%' }}
                  >
                    Cancel
                  </button>
                </div>

                {/* Right Panel - Map (only shown when drawing) */}
                {currentBatchIndex !== -1 && (
                  <div>
                    <div style={{
                      background: '#fef3c7',
                      padding: 'var(--space-md)',
                      borderRadius: 'var(--radius-md)',
                      marginBottom: 'var(--space-md)',
                      border: '1px solid #fcd34d'
                    }}>
                      <strong>Currently Drawing: </strong>
                      <span style={{ color: '#2196F3', fontWeight: '600' }}>
                        {batchWorkAreas[currentBatchIndex]?.name}
                      </span>
                      <p style={{ margin: '4px 0 0 0', fontSize: 'var(--font-size-sm)', color: '#92400e' }}>
                        🧲 <strong>Corner Snap ({SNAP_THRESHOLD_METERS}m):</strong> Move cursor near corners of existing areas to see yellow snap indicator. Click to snap! Double-click or click near first point to complete.
                      </p>
                    </div>

                    {/* Batch Drawing Controls */}
                    {!batchWorkAreas[currentBatchIndex]?.boundaries && (
                      <div style={{ marginBottom: 'var(--space-sm)', display: 'flex', gap: 'var(--space-sm)', flexWrap: 'wrap', alignItems: 'center' }}>
                        {!isBatchCustomDrawing && (
                          <button
                            type="button"
                            onClick={startBatchCustomDrawing}
                            className="btn btn-primary"
                            style={{ fontSize: 'var(--font-size-sm)' }}
                          >
                            🖊️ Start Drawing
                          </button>
                        )}
                        {isBatchCustomDrawing && (
                          <>
                            <span style={{ fontSize: 'var(--font-size-sm)', color: '#2196F3', fontWeight: '600' }}>
                              Points: {batchCustomDrawingPoints.length}
                            </span>
                            {batchCustomDrawingPoints.length >= 3 && (
                              <button
                                type="button"
                                onClick={handleCompleteBatchPolygon}
                                className="btn btn-success"
                                style={{
                                  fontSize: 'var(--font-size-sm)',
                                  background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                                  color: 'white',
                                  border: 'none'
                                }}
                              >
                                ✓ Complete Polygon
                              </button>
                            )}
                            <button
                              type="button"
                              onClick={() => {
                                setIsBatchCustomDrawing(false);
                                setBatchCustomDrawingPoints([]);
                                setBatchSnapIndicator(null);
                              }}
                              className="btn btn-secondary"
                              style={{ fontSize: 'var(--font-size-sm)' }}
                            >
                              ✕ Cancel
                            </button>
                            {batchSnapIndicator && (
                              <span style={{
                                fontSize: 'var(--font-size-sm)',
                                color: '#f59e0b',
                                fontWeight: '600',
                                background: '#fef3c7',
                                padding: '2px 8px',
                                borderRadius: '4px'
                              }}>
                                🧲 Snap to {batchSnapIndicator.type} of "{batchSnapIndicator.areaName}"
                              </span>
                            )}
                          </>
                        )}
                      </div>
                    )}

                    <LoadScript googleMapsApiKey={GOOGLE_MAPS_API_KEY} libraries={libraries}>
                      <GoogleMap
                        mapContainerStyle={{ ...mapContainerStyle, height: '450px' }}
                        center={mapCenter}
                        zoom={13}
                        onLoad={map => { mapRef.current = map }}
                        onClick={handleBatchMapClickForDrawing}
                        onMouseMove={handleBatchMapMouseMove}
                        onDblClick={handleBatchMapDoubleClick}
                        options={{
                          disableDoubleClickZoom: isBatchCustomDrawing
                        }}
                      >
                        {/* Display all completed batch boundaries */}
                        {batchWorkAreas.map((wa, index) => (
                          wa.boundaries && (
                            <Polygon
                              key={index}
                              paths={getPolygonPaths(wa.boundaries)}
                              options={{
                                fillColor: getBatchPolygonColor(index),
                                fillOpacity: 0.35,
                                strokeColor: getBatchPolygonColor(index),
                                strokeOpacity: 1,
                                strokeWeight: 2
                              }}
                            />
                          )
                        ))}

                        {/* Display EXISTING work areas from database (gray) */}
                        {workAreas
                          .filter(area => area.boundaries?.coordinates)
                          .map((area) => (
                            <Polygon
                              key={`existing-${area._id}`}
                              paths={getPolygonPaths(area.boundaries)}
                              options={{
                                fillColor: '#6b7280',
                                fillOpacity: 0.25,
                                strokeColor: '#6b7280',
                                strokeOpacity: 0.8,
                                strokeWeight: 2
                              }}
                            />
                          ))}

                        {/* Custom batch drawing: Polyline */}
                        {isBatchCustomDrawing && batchCustomDrawingPoints.length > 0 && (
                          <Polyline
                            path={batchCustomDrawingPoints.map(p => ({ lat: p.lat, lng: p.lng }))}
                            options={{
                              strokeColor: '#2196F3',
                              strokeWeight: 3,
                              strokeOpacity: 1
                            }}
                          />
                        )}

                        {/* Custom batch drawing: Markers */}
                        {isBatchCustomDrawing && batchCustomDrawingPoints.map((point, index) => (
                          <Marker
                            key={index}
                            position={{ lat: point.lat, lng: point.lng }}
                            clickable={false}
                            icon={{
                              path: window.google?.maps?.SymbolPath?.CIRCLE,
                              scale: index === 0 ? 10 : 7,
                              fillColor: point.snapped ? '#f59e0b' : '#2196F3',
                              fillOpacity: 1,
                              strokeColor: '#ffffff',
                              strokeWeight: 2
                            }}
                            title={point.snapped ? `Snapped to ${point.snapArea}` : `Point ${index + 1}`}
                          />
                        ))}

                        {/* Batch snap indicator */}
                        {isBatchCustomDrawing && batchSnapIndicator && (
                          <Circle
                            center={{ lat: batchSnapIndicator.lat, lng: batchSnapIndicator.lng }}
                            radius={15}
                            options={{
                              fillColor: '#f59e0b',
                              fillOpacity: 0.5,
                              strokeColor: '#f59e0b',
                              strokeWeight: 3,
                              strokeOpacity: 1,
                              clickable: false,
                              zIndex: 1
                            }}
                          />
                        )}

                        {/* First point indicator for batch (green circle) */}
                        {isBatchCustomDrawing && batchCustomDrawingPoints.length >= 3 && (
                          <Circle
                            center={{ lat: batchCustomDrawingPoints[0].lat, lng: batchCustomDrawingPoints[0].lng }}
                            radius={50}
                            options={{
                              fillColor: '#10b981',
                              fillOpacity: 0.4,
                              strokeColor: '#10b981',
                              strokeWeight: 3,
                              strokeOpacity: 1,
                              clickable: false,
                              zIndex: 1
                            }}
                          />
                        )}
                      </GoogleMap>
                    </LoadScript>

                    {/* Legend */}
                    <div style={{
                      display: 'flex',
                      gap: 'var(--space-lg)',
                      marginTop: 'var(--space-sm)',
                      fontSize: 'var(--font-size-sm)',
                      color: 'var(--color-text-secondary)',
                      flexWrap: 'wrap'
                    }}>
                      <span><span style={{ color: '#2196F3' }}>●</span> Drawing Points</span>
                      <span><span style={{ color: '#f59e0b' }}>●</span> Snapped Points</span>
                      <span><span style={{ color: '#10b981' }}>■</span> Completed Areas</span>
                      <span><span style={{ color: '#6b7280' }}>■</span> Existing Areas</span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default WorkAreaManagement;
